import { useCallback } from "react"
import { getHelpCenterBase } from "../urlUtils"

export function useNavigation(contentLoader) {
  const {
    loadArticle,
    loadCategory,
    setExpandedCategories,
    expandedCategories,
    setActiveContentSlug,
    setActiveContentType,
    setSelectedContent,
    setContentType,
    setBreadcrumbItems,
    updateUrl,
    loadContentFromUrl,
  } = contentLoader

  const navigateTo = useCallback(
    async (type, slug = null, parentPath = "") => {
      try {
        if (type === "article") {
          const fullCategoryPath = parentPath || ""

          if (fullCategoryPath) {
            const pathParts = fullCategoryPath.split("/")
            const newExpandedCategories = { ...expandedCategories }
            for (let i = 0; i < pathParts.length; i++) {
              newExpandedCategories[pathParts[i]] = true
            }
            setExpandedCategories(newExpandedCategories)
          }

          const articleData = await loadArticle(slug, parentPath)
          if (articleData) {
            updateUrl("article", slug, parentPath)
            setActiveContentSlug(slug)
            setActiveContentType("article")
            setSelectedContent(articleData)
            setContentType("article")
          }
        } else if (type === "category") {
          const fullPath = parentPath ? `${parentPath}/${slug}` : slug

          if (fullPath) {
            const pathParts = fullPath.split("/")
            const newExpandedCategories = { ...expandedCategories }
            for (let i = 0; i < pathParts.length; i++) {
              newExpandedCategories[pathParts[i]] = true
            }
            setExpandedCategories(newExpandedCategories)
          }

          const categoryData = await loadCategory(fullPath)
          if (categoryData) {
            updateUrl("category", slug, parentPath)
            setActiveContentSlug(fullPath)
            setActiveContentType("category")
            setSelectedContent(categoryData)
            setContentType("category")
          }
        } else {
          setSelectedContent(null)
          setContentType(null)
          setBreadcrumbItems([{ label: "Help Center", link: getHelpCenterBase() }])
          updateUrl(null)
          setActiveContentSlug(null)
          setActiveContentType(null)
        }
      } catch (err) {
        console.error("Error navigating to content:", err)
      }
    },
    [
      expandedCategories,
      loadArticle,
      loadCategory,
      setActiveContentSlug,
      setActiveContentType,
      setContentType,
      setExpandedCategories,
      setSelectedContent,
      setBreadcrumbItems,
      updateUrl,
    ]
  )

  const handleArticleSelect = useCallback(
    async (article) => {
      if (!article || !article.slug) return
      await navigateTo("article", article.slug, article.categoryPath)
    },
    [navigateTo]
  )

  const handleCategorySelect = useCallback(
    async (category) => {
      if (!category || !category.slug) return
      setExpandedCategories((prev) => ({
        ...prev,
        [category.slug]: category.shouldExpand,
      }))

      if (category.shouldExpand) {
        await navigateTo("category", category.slug, category.parentPath)
      }
    },
    [navigateTo, setExpandedCategories]
  )

  return {
    navigateTo,
    handleArticleSelect,
    handleCategorySelect,
    loadContentFromUrl,
  }
}

export default useNavigation
